import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Form, Input, Select, Switch, Radio, Alert, Space, Tooltip, Button, Tag,
} from 'antd';
import { captureAnalytics } from '../../actions/preload';
import DraggableModal from './DraggableModal';
import RuleDocumentDefaultsFields from './RuleDocumentDefaultsFields';

const { Option } = Select;

// EMA ordering-provider matching only runs on documents in "Results - *"
// categories (RunAutoUploadForReceivedRecordWorker), so the rule only needs the
// setting when its category filter can reach one of them.
export const categoryFilterReachesResults = (categoryFilter, categoryIds, categories) => {
  const selectedIds = new Set(categoryIds);
  return (categories || []).some((category) => {
    if (!(category.emr_category || '').toLowerCase().startsWith('results - ')) return false;
    if (categoryFilter === 'only') return selectedIds.has(category.id);
    if (categoryFilter === 'except') return !selectedIds.has(category.id);
    return true;
  });
};

// Maps an existing rule (or null) to the modal's local form state. A new rule
// starts from the org's EMA results ordering-provider setting.
export const initialStateFromRule = (rule, emaResults = {}) => {
  const defaultOnNoMatch = emaResults.default_on_no_match || 'best_effort';
  if (!rule) {
    return {
      labelFilter: null,
      name: '',
      sourceType: 'org',
      sourceId: null,
      action: 'upload',
      categoryFilter: 'all',
      categoryIds: [],
      autoArchive: false,
      autoArchiveScope: 'inbox_only',
      emaResultsEnabled: !!emaResults.default_enabled,
      emaOnNoMatch: defaultOnNoMatch,
      documentDefaults: {},
      defaultsTouched: false,
      active: true,
    };
  }
  let categoryFilter = 'all';
  if (rule.category_match_mode === 'all_except') {
    categoryFilter = (rule.category_ids || []).length > 0 ? 'except' : 'categorized';
  } else if ((rule.category_ids || []).length > 0) categoryFilter = 'only';
  return {
    labelFilter: null,
    name: rule.name || '',
    sourceType: ['FaxNumber', 'Email', 'Department'].includes(rule.source_type) ? rule.source_type : 'org',
    sourceId: rule.source_id || null,
    action: rule.action || 'upload',
    categoryFilter,
    categoryIds: rule.category_ids || [],
    autoArchive: !!rule.auto_archive,
    autoArchiveScope: rule.auto_archive_scope || 'inbox_only',
    emaResultsEnabled: !!rule.ema_results_enabled,
    emaOnNoMatch: rule.ema_on_no_match || defaultOnNoMatch,
    documentDefaults: rule.document_defaults || {},
    defaultsTouched: false,
    active: rule.active !== false,
  };
};

// Create/edit modal for a self-serve AutoUploadRule, modeled on AddTaskRoutingRuleModal.
// Supports org-wide, per-fax-line, per-email-inbox, and per-department
// sources, a category filter, a label filter, the action, and the auto-archive
// post-action. Other condition kinds (caller, from number, sender email) are
// import-managed and left untouched.
export class UnconnectedAddAutoUploadRuleModal extends Component {
  constructor(props) {
    super(props);
    this.state = initialStateFromRule(props.editingRule, props.emaResults);
    this.state.emaResultsTouched = false;
    // Fax lines come from the cached login payload, not a network call — this
    // matches ToggleFeatures/ModmedQueuedFaxSettings and avoids a 404 + unmount warning.
    this.state.faxNumbers = JSON.parse(localStorage.getItem('associated_fax_numbers') || '[]');
  }

  // Options only supply display names. The server owns compatibility and ID
  // resolution, so delayed options cannot lock the form or overwrite a draft.
  currentLabelFilter = () => this.state.labelFilter || this.props.editingRule?.label_filter || {
    editable: true, mode: 'any', label_ids: [], missing_label_count: 0,
  };

  // Untouched new rules follow metadata as it arrives. Existing rules and
  // deliberate edits retain their own policy across background refreshes.
  emaResultsPolicy = () => {
    if (!this.props.editingRule && !this.state.emaResultsTouched) {
      return {
        emaResultsEnabled: !!this.props.emaResults?.default_enabled,
        emaOnNoMatch: this.props.emaResults?.default_on_no_match || 'best_effort',
      };
    }
    const { emaResultsEnabled, emaOnNoMatch } = this.state;
    return { emaResultsEnabled, emaOnNoMatch };
  };

  handleEmaResultsChange = (emaResultsEnabled) => {
    captureAnalytics('auto_upload_rule_ema_results_matching_changed', { enabled: emaResultsEnabled });
    const { emaOnNoMatch } = this.emaResultsPolicy();
    this.setState({ emaResultsEnabled, emaOnNoMatch, emaResultsTouched: true });
  };

  handleEmaOnNoMatchChange = (emaOnNoMatch) => {
    captureAnalytics('auto_upload_rule_ema_results_no_match_changed', { on_no_match: emaOnNoMatch });
    const { emaResultsEnabled } = this.emaResultsPolicy();
    this.setState({ emaResultsEnabled, emaOnNoMatch, emaResultsTouched: true });
  };

  isValid = () => {
    const {
      sourceType, sourceId, categoryFilter, categoryIds,
    } = this.state;
    if (sourceType !== 'org' && !sourceId) return false;
    if (['only', 'except'].includes(categoryFilter) && categoryIds.length === 0) return false;
    const filter = this.currentLabelFilter();
    if (this.state.labelFilter && filter.editable && filter.mode !== 'any'
      && (filter.label_ids.length === 0 || filter.missing_label_count > 0)) return false;
    return true;
  };

  buildPayload = () => {
    const {
      name, sourceType, sourceId, action, categoryFilter, categoryIds,
      autoArchive, autoArchiveScope, active, documentDefaults, defaultsTouched,
      labelFilter,
    } = this.state;
    const { emaResultsEnabled, emaOnNoMatch } = this.emaResultsPolicy();
    const uploadsDocument = ['upload', 'upload_and_task'].includes(action);
    const payload = {
      name: name || null,
      source_type: sourceType === 'org' ? null : sourceType,
      source_id: sourceType === 'org' ? null : sourceId,
      action,
      category_match_mode: ['categorized', 'except'].includes(categoryFilter) ? 'all_except' : 'any',
      category_ids: ['all', 'categorized'].includes(categoryFilter) ? [] : categoryIds,
      auto_archive: uploadsDocument && autoArchive,
      auto_archive_scope: uploadsDocument && autoArchive ? autoArchiveScope : null,
      active,
    };
    // Only send the label filter when the admin changed it, so saving an
    // imported rule's other fields never rewrites its stored label conditions.
    if (labelFilter?.editable) {
      payload.label_filter = labelFilter.mode === 'any' ? null : { mode: labelFilter.mode, label_ids: labelFilter.label_ids };
    }
    // The API ignores these keys without the EMA results feature, so only send them when it is on.
    if (this.props.emaResults?.available) {
      payload.ema_results_enabled = uploadsDocument && emaResultsEnabled;
      payload.ema_on_no_match = uploadsDocument && emaResultsEnabled ? emaOnNoMatch : null;
    }
    const storedDefaults = this.props.editingRule?.document_defaults || {};
    if (action === 'upload' && defaultsTouched) payload.document_defaults = documentDefaults;
    if (action !== 'upload' && Object.keys(storedDefaults).length > 0) payload.document_defaults = {};
    return payload;
  };

  handleOk = () => {
    const { onSubmit, editingRule } = this.props;
    onSubmit(this.buildPayload(), editingRule && editingRule.id);
  };

  handleSourceChange = (event) => {
    const sourceType = event.target.value;
    captureAnalytics('auto_upload_rule_source_type_selected', { source_type: sourceType });
    this.setState({ sourceType, sourceId: null });
  };

  handleDepartmentSelect = (sourceId) => {
    captureAnalytics('auto_upload_rule_department_selected', { source_type: 'Department' });
    this.setState({ sourceId });
  };

  handleLabelFilterChange = (mode) => {
    captureAnalytics('auto_upload_rule_label_filter_selected', { label_filter: mode });
    const filter = this.currentLabelFilter();
    this.setState({ labelFilter: { ...filter, mode, missing_label_count: mode === 'any' ? 0 : filter.missing_label_count } });
  };

  handleLabelIdsChange = (labelIds) => {
    const nextLabelIds = labelIds || [];
    const filter = this.currentLabelFilter();
    const previousIds = new Set(filter.label_ids);
    const nextIds = new Set(nextLabelIds);
    captureAnalytics('auto_upload_rule_labels_changed', {
      label_filter: filter.mode,
      selected_count: nextLabelIds.length,
      added_count: nextLabelIds.filter((id) => !previousIds.has(id)).length,
      removed_count: filter.label_ids.filter((id) => !nextIds.has(id)).length,
    });
    this.setState({ labelFilter: { ...filter, label_ids: nextLabelIds } });
  };

  handleRemoveMissingLabels = () => {
    const filter = this.currentLabelFilter();
    captureAnalytics('auto_upload_rule_missing_labels_removed', {
      removed_count: filter.missing_label_count, remaining_count: filter.label_ids.length,
    });
    this.setState({
      labelFilter: {
        ...filter, missing_label_count: 0, mode: filter.label_ids.length ? filter.mode : 'any',
      },
    });
  };

  handleDocumentDefaultsChange = (next) => {
    const { documentDefaults } = this.state;
    this.props.documentDefaultFields.forEach(({ internal_name: fieldKey }) => {
      if (documentDefaults[fieldKey] !== next[fieldKey]) {
        captureAnalytics('auto_upload_rule_document_default_changed', {
          field_key: fieldKey,
          configured: Object.prototype.hasOwnProperty.call(next, fieldKey),
        });
      }
    });
    this.setState({ documentDefaults: next, defaultsTouched: true });
  };

  clearAllDocumentDefaults = () => {
    const { editingRule, onSubmit } = this.props;
    captureAnalytics('auto_upload_rule_document_defaults_cleared');
    onSubmit({ ...this.buildPayload(), document_defaults: {} }, editingRule.id);
  };

  renderLabelTag = ({ value, closable, onClose }) => {
    const label = this.props.labels.find((entry) => entry.id === value);
    return (
      <Tag
        color={label?.color}
        closable={closable}
        onClose={onClose}
        style={{ color: label?.is_dark_text ? 'black' : 'white', marginRight: 4 }}
      >
        {label?.name || value}
      </Tag>
    );
  };

  renderLabelFilter() {
    const { labels } = this.props;
    const {
      mode: labelFilter, label_ids: labelIds, editable: labelsEditable, missing_label_count: missingCount,
    } = this.currentLabelFilter();

    if (!labelsEditable) {
      return (
        <Form.Item label="Labels">
          <Alert
            type="info"
            showIcon
            message="This rule's conditions were imported from your previous settings and can't be edited here. They're kept as-is when you save."
          />
        </Form.Item>
      );
    }

    return (
      <>
        {missingCount > 0 && (
          <Alert
            type="warning"
            showIcon
            message="This filter references deleted labels."
            description={(
              <>
                <p>
                  {labelIds.length ? 'Remove the missing labels to edit this filter.'
                    : 'Removing the missing labels will clear this filter. Choose replacement labels before saving if you still want to filter documents.'}
                </p>
                <Button onClick={this.handleRemoveMissingLabels}>Remove missing labels</Button>
              </>
            )}
          />
        )}
        <Form.Item
          label="Labels"
          extra={labelFilter === 'any'
            ? null
            : 'Labels are checked when the document is ready to upload. Labels added automatically count; labels added by hand after the upload do not.'}
        >
          <Select value={labelFilter} onChange={this.handleLabelFilterChange}>
            <Option value="any">Any labels (don't filter by label)</Option>
            <Option value="has_any">Only documents with at least one of these labels</Option>
            <Option value="has_none">Only documents with none of these labels</Option>
          </Select>
        </Form.Item>

        {labelFilter !== 'any' && (
          <Form.Item label={labelFilter === 'has_any' ? 'Has any of' : 'Has none of'} required>
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select labels"
              optionFilterProp="label"
              value={labelIds}
              onChange={this.handleLabelIdsChange}
              tagRender={this.renderLabelTag}
            >
              {labels.map((label) => (
                <Option key={label.id} value={label.id} label={label.name}>
                  <Tag color={label.color} style={{ color: label.is_dark_text ? 'black' : 'white' }}>
                    {label.name}
                  </Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </>
    );
  }

  render() {
    const {
      visible, onCancel, editingRule, categories, confirmLoading, canCreateTasks, emailSources, departments,
      documentDefaultFields, documentDefaultsCredentialId, documentDefaultsUnavailableReason, saveError, emaResults,
    } = this.props;
    const {
      name, sourceType, sourceId, action, categoryFilter, categoryIds,
      autoArchive, autoArchiveScope, active, faxNumbers, documentDefaults,
    } = this.state;
    const { emaResultsEnabled, emaOnNoMatch } = this.emaResultsPolicy();
    const uploadsDocument = ['upload', 'upload_and_task'].includes(action);
    const showEmaResults = uploadsDocument && !!emaResults?.available
      && categoryFilterReachesResults(categoryFilter, categoryIds, categories);
    const hasStoredDefaults = Object.keys(editingRule?.document_defaults || {}).length > 0;
    const showDocumentDefaults = action === 'upload' && (documentDefaultFields.length > 0 || hasStoredDefaults);
    let unavailableMessage = 'Document defaults are unavailable for this integration.';
    if (documentDefaultsUnavailableReason === 'no_credential') {
      unavailableMessage = 'No EMR credential is configured.';
    } else if (documentDefaultsUnavailableReason === 'multiple_credentials') {
      unavailableMessage = 'More than one EMR credential is configured, so defaults can\'t be edited here.';
    }
    const providerError = saveError?.code === 'assignee_invalid' ? saveError.message : null;
    const providerVerificationUnsupported = saveError?.code === 'assignee_verification_unsupported';
    const providerVerificationUnavailable = saveError?.code === 'assignee_verification_unavailable';
    const showsInlineDocumentDefaultsError = showDocumentDefaults && documentDefaultFields.length > 0
      && (providerVerificationUnavailable || providerVerificationUnsupported || providerError);
    const uploadAndTaskLabel = localStorage.m8_auto_upload_emr === 'Kareo'
      ? 'Upload to Tebra and create a note' : 'Upload to the EMR and create a task';

    return (
      <DraggableModal
        visible={visible}
        title={editingRule ? 'Edit auto-upload rule' : 'Add auto-upload rule'}
        onCancel={onCancel}
        onOk={this.handleOk}
        okText={editingRule ? 'Save' : 'Create'}
        okButtonProps={{ disabled: !this.isValid(), loading: confirmLoading }}
        width={560}
        destroyOnClose
      >
        <Form layout="vertical">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Rules are evaluated by priority (top first). The first matching rule decides what happens to a document."
            description="A department rule covers every fax line and email inbox that department owns. If a line-specific rule also matches, the higher rule wins."
          />
          {saveError && !showsInlineDocumentDefaultsError && (
            <Alert
              style={{ marginBottom: 16 }}
              type="error"
              showIcon
              message={saveError.message}
            />
          )}

          <Form.Item label="Name (optional)">
            <Input
              placeholder="e.g. Referrals on the main line"
              value={name}
              onChange={(e) => this.setState({ name: e.target.value })}
            />
          </Form.Item>

          <Form.Item label="Applies to">
            <Radio.Group
              value={sourceType}
              onChange={this.handleSourceChange}
            >
              <Space direction="vertical">
                <Radio value="org">All documents (org-wide default)</Radio>
                <Radio value="FaxNumber">A specific fax line</Radio>
                <Radio value="Email">A specific email inbox</Radio>
                <Radio value="Department">A specific department</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {sourceType === 'FaxNumber' && (
            <Form.Item label="Fax line" required>
              <Select
                showSearch
                placeholder="Select a fax line"
                optionFilterProp="children"
                value={sourceId}
                onChange={(value) => this.setState({ sourceId: value })}
              >
                {faxNumbers.map((faxEntry) => {
                  const [number, ownerName, , , faxId] = faxEntry || [];
                  if (!faxId) return null;
                  return (
                    <Option key={faxId} value={faxId}>
                      {number}
                      {ownerName ? ` — ${ownerName}` : ''}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}

          {sourceType === 'Email' && (
            <Form.Item label="Email inbox" required>
              <Select
                showSearch
                placeholder="Select an email inbox"
                optionFilterProp="children"
                value={sourceId}
                onChange={(value) => this.setState({ sourceId: value })}
              >
                {emailSources.map((email) => (
                  <Option key={email.id} value={email.id}>
                    {email.email_address}
                    {email.owner_name ? ` — ${email.owner_name}` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {sourceType === 'Department' && (
            <Form.Item label="Department" required>
              <Select
                showSearch
                placeholder="Select a department"
                optionFilterProp="children"
                value={sourceId}
                onChange={this.handleDepartmentSelect}
              >
                {departments.map((department) => (
                  <Option key={department.id} value={department.id}>{department.name}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="When a document matches, then"
            extra={action === 'upload_and_task'
              ? 'The template is picked per document by your routing rules or the fax line’s category template. If no template applies, this rule is skipped and the next rule is tried.'
              : null}
          >
            <Select
              value={action}
              onChange={(value) => this.setState({ action: value })}
            >
              <Option value="upload">Upload to the EMR</Option>
              <Option value="upload_and_task" disabled={!canCreateTasks}>
                {canCreateTasks ? (
                  uploadAndTaskLabel
                ) : (
                  <Tooltip title="Requires the task auto-upload feature. Contact support to enable it.">
                    {uploadAndTaskLabel}
                  </Tooltip>
                )}
              </Option>
              <Option value="skip">Do not auto-upload (skip)</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Document categories">
            <Select value={categoryFilter} onChange={(value) => this.setState({ categoryFilter: value })}>
              <Option value="all">All categories</Option>
              <Option value="categorized">All categorized documents</Option>
              <Option value="only">Only the selected categories</Option>
              <Option value="except">All categories except the selected</Option>
            </Select>
          </Form.Item>

          {['only', 'except'].includes(categoryFilter) && (
            <Form.Item label="Categories" required>
              <Select
                mode="multiple"
                showSearch
                placeholder="Select categories"
                optionFilterProp="children"
                value={categoryIds}
                onChange={(value) => this.setState({ categoryIds: value })}
              >
                {(categories || []).map((cat) => (
                  <Option key={cat.id} value={cat.id}>{cat.emr_category}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {this.renderLabelFilter()}

          {uploadsDocument && (
            <Form.Item
              label="Archive after successful upload"
              extra="Archive matching received faxes after the EMR confirms a successful upload."
            >
              <Switch
                checked={autoArchive}
                onChange={(checked) => this.setState({ autoArchive: checked })}
              />
            </Form.Item>
          )}

          {uploadsDocument && autoArchive && (
            <Form.Item label="Archive records from">
              <Select
                value={autoArchiveScope}
                onChange={(value) => this.setState({ autoArchiveScope: value })}
              >
                <Option value="inbox_only">Inbox only</Option>
                <Option value="rm_only">Referral Management only</Option>
                <Option value="both">Both</Option>
              </Select>
            </Form.Item>
          )}

          {showEmaResults && (
            <Form.Item
              label="Match ordering provider for Results uploads"
              extra="For Results categories, match the provider named on the document to an active ModMed provider and send it as the ordering provider."
            >
              <Switch
                checked={emaResultsEnabled}
                onChange={this.handleEmaResultsChange}
              />
            </Form.Item>
          )}

          {showEmaResults && emaResultsEnabled && (
            <Form.Item label="When no provider matches">
              <Select
                value={emaOnNoMatch}
                onChange={this.handleEmaOnNoMatchChange}
              >
                <Option value="best_effort">Upload without an ordering provider</Option>
                <Option value="hold_for_review">Hold for manual review</Option>
              </Select>
            </Form.Item>
          )}

          {showDocumentDefaults && (
            <section aria-label="Document defaults">
              <h3 style={{ marginTop: 20 }}>Document defaults</h3>
              {providerVerificationUnavailable && (
                <Alert
                  style={{ marginBottom: 12 }}
                  type="warning"
                  showIcon
                  message="Could not verify the assignee with eCW right now. Try again in a minute."
                />
              )}
              {documentDefaultFields.length > 0 ? (
                <>
                  {providerVerificationUnsupported && (
                    <Alert
                      style={{ marginBottom: 12 }}
                      type="warning"
                      showIcon
                      message="eCW assignee verification is not deployed yet. Clear the assignee to save the other document defaults."
                    />
                  )}
                  <RuleDocumentDefaultsFields
                    fields={documentDefaultFields}
                    value={documentDefaults}
                    credentialId={documentDefaultsCredentialId}
                    providerError={providerError}
                    onChange={this.handleDocumentDefaultsChange}
                  />
                </>
              ) : (
                <>
                  <Alert type="info" showIcon message={unavailableMessage} />
                  <ul>
                    {Object.entries(editingRule.document_defaults_labels || editingRule.document_defaults || {}).map(([key, value]) => (
                      <li key={key}>{`${key.replace(/_/g, ' ')}: ${value}`}</li>
                    ))}
                  </ul>
                  <Button onClick={this.clearAllDocumentDefaults} loading={confirmLoading}>Clear all defaults</Button>
                </>
              )}
            </section>
          )}

          <Form.Item label="Active">
            <Switch checked={active} onChange={(checked) => this.setState({ active: checked })} />
          </Form.Item>
        </Form>
      </DraggableModal>
    );
  }
}

UnconnectedAddAutoUploadRuleModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  editingRule: PropTypes.object,
  categories: PropTypes.array,
  confirmLoading: PropTypes.bool,
  canCreateTasks: PropTypes.bool,
  emailSources: PropTypes.array,
  labels: PropTypes.array,
  departments: PropTypes.array,
  documentDefaultFields: PropTypes.array,
  documentDefaultsCredentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  documentDefaultsUnavailableReason: PropTypes.string,
  emaResults: PropTypes.shape({
    available: PropTypes.bool,
    default_enabled: PropTypes.bool,
    default_on_no_match: PropTypes.string,
  }),
  saveError: PropTypes.shape({
    status: PropTypes.number,
    code: PropTypes.string,
    message: PropTypes.string,
  }),
};

UnconnectedAddAutoUploadRuleModal.defaultProps = {
  editingRule: null,
  categories: [],
  confirmLoading: false,
  canCreateTasks: false,
  emailSources: [],
  labels: [],
  departments: [],
  documentDefaultFields: [],
  documentDefaultsCredentialId: null,
  documentDefaultsUnavailableReason: null,
  emaResults: { available: false },
  saveError: null,
};

export default UnconnectedAddAutoUploadRuleModal;
